import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { auth2Actions } from '@core/auth2/store/auth2.actions';
import { recoverThroughEdgeAuthGateIfClosed } from '@core/auth2/edge-auth-gate';
import { Store } from '@ngrx/store';
import { EMPTY, from, Observable, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { APP_CONFIG } from '@cco-apps/cco-frontend';

/**
 * Statuses that might mean the edge auth gate turned the request away rather than
 * the backend answering it. None of them is conclusive, which is why each costs a
 * probe before it is acted on:
 *
 * - `504` is the Angular service worker's synthetic response when its own fetch
 *   fails, but is also a genuine gateway timeout.
 * - `0` is a cross-origin redirect with no service worker to intercept it, but is
 *   also what being offline looks like.
 * - `401` is the gate's answer to a request that asked for JSON (see
 *   sendCredentialsInterceptor) — and equally the backend's answer to an expired
 *   application session. Guessing wrong here strands the user: the app's own login
 *   cannot get past the gate, because submitting a tenant code is itself an API
 *   request that the gate rejects the same way.
 */
const EDGE_AUTH_GATE_STATUSES = [ 0, 401, 504 ];

export function redirectToLoginInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const store = inject(Store);
  const appConfig = inject(APP_CONFIG);
  const apiBaseUrl = new URL(appConfig.apiBaseUrl, window.location.origin).toString();

  return next(req).pipe(
    catchError((err: any): Observable<HttpEvent<unknown>> => {
      const requestUrl = new URL(req.url, window.location.origin).toString();

      if (err instanceof HttpErrorResponse && requestUrl.startsWith(apiBaseUrl)) {
        if (EDGE_AUTH_GATE_STATUSES.includes(err.status)) {
          return from(recoverThroughEdgeAuthGateIfClosed()).pipe(
            switchMap((recovered) => {
              if (recovered) {
                return EMPTY;
              }

              // The gate is open, so a 401 really is an expired app session.
              if (err.status === 401) {
                store.dispatch(auth2Actions.authenticateUser());
                return EMPTY;
              }

              return throwError(() => err);
            }),
          );
        }
      }

      throw err;
    }),
  );
}
